import { Component, ViewChild, ElementRef, AfterViewInit, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonDataService } from './Services/commonDataService';
import { NavService } from './Services/nav.service';
import { Global } from './Shared/global';
import { Screen } from './Models/Screen';
import { UntypedFormControl } from '@angular/forms';
import { Organisation } from './Models/Common/Organisation';
import { User } from './Models/FitnessBox/User';
import { AppContext } from './Shared/common';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { ApplicationConfiguration } from './Models/ApplicationConfiguration';
import { IdleUserService } from './Services/idle-user.service';
import { AuthService } from './Services/auth.service';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['app.component.css']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  org: string = '';
  user: string = '';
  indLoading = false;
  navItems: Screen[] = [];
  displayNavigation = false;
  navVisibilityFilter = { IsVisible: true };
  currentScreen: Screen = null;
  invalidRedirect: boolean = false;
  isAuthenticated = false;
  isInitialized = false;

  mode = new UntypedFormControl('over');
  shouldRun = true;
  @ViewChild('appDrawer', { static: false }) appDrawer: ElementRef;
  isUserIdle: boolean = false;

  // ✅ Simplified state management
  authCheckComplete = false;  // Auth check done?
  userLoggedIn = false;       // User actually logged in?

  private destroy$ = new Subject<void>();
  private loadingCounter = 0;

  constructor(
    private _service: CommonDataService,
    private navService: NavService,
    private elementRef: ElementRef,
    public appctx: AppContext,
    private router: Router,
    private route: ActivatedRoute,
    private idleUserService: IdleUserService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.org = this.elementRef.nativeElement.getAttribute('org') || '';
    this.user = this.elementRef.nativeElement.getAttribute('user') || '';
    this.appctx.CurrentOrg = new Organisation();
    this.appctx.CurrentUser = new User();
    this.processAppConfig();
  }

  ngOnInit(): void {
    Global.Log('[AppComponent] ngOnInit called');

    // ✅ Initial state: not checked yet
    this.authCheckComplete = false;
    this.userLoggedIn = false;
    this.isInitialized = false;
    this.displayNavigation = false;
    this.cdr.markForCheck();

    this.setupIdleUser();
    this.setupRouterSubscription();

    // Check authentication
    this.isAuthenticated = this.authService.isLoggedIn();
    if (this.isAuthenticated) {
      Global.Log('[AppComponent] User already authenticated on init');
      this.initializeUserContext();
    } else if (this.org && this.user) {
      Global.Log('[AppComponent] Legacy mode detected');
      this.initializeFromLegacyMode();
    } else {
      // Not authenticated - show login page
      Global.Log('[AppComponent] Not authenticated, showing login page');
      this.authCheckComplete = true;  // ✅ Auth check is done
      this.userLoggedIn = false;      // ✅ User not logged in

      if (!this.isPublicRoute(this.router.url)) {
        this.router.navigate(['/login']);
      }

      this.cdr.markForCheck();
      this.setupAuthSubscription();
    }
  }

  ngAfterViewInit(): void {
    this.assignAppDrawer();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Assign appDrawer to NavService
   */
  private assignAppDrawer(): void {
    if (this.appDrawer) {
      Global.Log('[AppComponent] Assigning appDrawer to NavService');
      this.navService.appDrawer = this.appDrawer;
    }
  }

  private isPublicRoute(url: string): boolean {
    const publicRoutes = ['login', 'org-selector', 'org-input', 'redirector', 'restrictedaccess', 'register', 'forgot-password'];
    const normalizedUrl = url.replace(/^\//, '').split('?')[0].split('/')[0];
    return publicRoutes.includes(normalizedUrl);
  }

  private setupAuthSubscription(): void {
    this.authService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAuthenticated => {
        Global.Log('[AppComponent] Auth state changed:', isAuthenticated);

        if (isAuthenticated && !this.userLoggedIn) {
          Global.Log('[AppComponent] User just logged in, initializing...');
          // ✅ Reset navigation state before reinitializing
          this.resetNavigationState();
          this.initializeUserContext();
        } else if (!isAuthenticated && this.userLoggedIn) {
          Global.Log('[AppComponent] User logged out, cleaning up...');
          // ✅ Properly clean up on logout
          this.handleLogout();
        }
      });
  }

  private initializeFromLegacyMode(): void {
    try {
      const currentOrg = JSON.parse(this.org.replaceAll("'", "\""));
      const currentUser = JSON.parse(this.user.replaceAll("'", "\""));

      this.authService['currentUserSubject'].next(currentUser);
      this.authService['currentOrgSubject'].next(currentOrg);

      this.initializeUserContext();
    } catch (e) {
      Global.LogError('[AppComponent] Legacy parsing failed:', e);
      this.authCheckComplete = true;
      this.userLoggedIn = false;
      this.redirectToLogin();
    }
  }

  /**
   * ✅ Initialize after successful login
   */
  private initializeUserContext(): void {
    Global.Log('[AppComponent] Initializing user context...');

    // Prevent double initialization
    if (this.userLoggedIn) {
      Global.LogWarning('[AppComponent] User context already initialized');
      return;
    }

    const currentUser = this.authService.getCurrentUser();
    const currentOrg = this.authService.getCurrentOrganization();

    if (!currentUser?.ID || !currentOrg?.OrgId) {
      Global.LogWarning('[AppComponent] User or organization data missing');
      this.authCheckComplete = true;
      this.userLoggedIn = false;
      return;
    }

    // Set user and org context
    this.appctx.CurrentUser = currentUser as User;
    Global.CurrentUser = currentUser as User;
    this.appctx.CurrentOrg = this.convertAuthOrgToAppOrg(currentOrg);
    Global.CurrentOrg = this.appctx.CurrentOrg;

    // ✅ Update state flags - IMPORTANT: Set displayNavigation BEFORE triggering change detection
    this.authCheckComplete = true;   // Auth check complete
    this.userLoggedIn = true;        // User is logged in
    this.isInitialized = true;
    this.displayNavigation = true;   // ✅ THIS MUST BE SET TO TRUE

    Global.Log('[AppComponent] User context initialized, showing app layout');
    this.cdr.markForCheck();

    // Schedule appDrawer assignment after view updates
    setTimeout(() => {
      this.assignAppDrawer();
      this.cdr.markForCheck();
    }, 0);

    // Load navigation and app config
    this.Load();
  }

  /**
   * ✅ NEW: Reset navigation state before re-login
   */
  private resetNavigationState(): void {
    Global.Log('[AppComponent] Resetting navigation state...');
    
    this.navItems = [];
    this.displayNavigation = false;
    this.isInitialized = false;
    this.indLoading = false;
    this.currentScreen = null;
    
    // ✅ Close sidenav if it's open
    if (this.navService.appDrawer) {
      try {
        this.navService.appDrawer.close();
      } catch (e) {
        Global.LogError('[AppComponent] Error closing drawer:', e);
      }
    }
    
    // ✅ Collapse all expanded menu items
    this.navService.collapseAll();
    
    this.cdr.markForCheck();
  }

  /**
   * ✅ NEW: Handle logout properly
   */
  private handleLogout(): void {
    Global.Log('[AppComponent] Handling logout...');
    
    this.resetNavigationState();
    this.userLoggedIn = false;
    this.authCheckComplete = true;
    
    this.cdr.markForCheck();
  }

  private setupIdleUser(): void {
    this.idleUserService.userInactive
      .pipe(takeUntil(this.destroy$))
      .subscribe(isIdle => {
        if (isIdle) {
          Global.Log('[AppComponent] User idle detected, logging out...');
          
          this.authService.logout().subscribe({
            next: () => {
              Global.Log('[AppComponent] Idle logout successful');
              this.handleLogout();
              this.redirectToLogin();
            },
            error: (error) => {
              Global.LogError('[AppComponent] Idle logout error:', error);
              this.handleLogout();
              this.redirectToLogin();
            }
          });
        }
      });
  }

  private setupRouterSubscription(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          if (this.navItems && this.navItems.length > 0) {
            this.invalidRedirect = false;
            this.updateMenuSelection(this.router.url);
          } else {
            this.invalidRedirect = true;
          }
          if (this.isInitialized) {
            this.validateAccess(this.router.url);
          }
        }
      });
  }

  trackByRoutingUrl(index: number, item: Screen): any {
    return item.RoutingUrl || index;
  }

  private updateMenuSelection(url: string): void {
    let normalizedUrl = decodeURIComponent(url).replace(/^\//, '');
    normalizedUrl = normalizedUrl.split('?')[0].split('/edit')[0].split('/upgrade')[0];
    let urlPart = normalizedUrl.split('/')[0];

    this.clearMenuSelections(this.navItems);

    this.navItems.forEach(item => {
      if (item.RoutingUrl === normalizedUrl || item.RoutingUrl === urlPart) {
        item.IsSelected = true;
        this.appctx.CurrentScreen = this.currentScreen = item;
      }

      if (item.children?.length > 0) {
        item.children.forEach(child => {
          if (child.RoutingUrl === normalizedUrl || child.RoutingUrl === urlPart) {
            child.IsSelected = true;
            item.IsSelected = true;
            this.appctx.CurrentScreen = this.currentScreen = child;
          }
        });
      }
    });

    this.cdr.markForCheck();
  }

  private clearMenuSelections(items: Screen[]): void {
    items.forEach(item => {
      item.IsSelected = false;
      if (item.children?.length > 0) {
        this.clearMenuSelections(item.children);
      }
    });
  }

  private convertAuthOrgToAppOrg(authOrg: Organisation): Organisation {
    const org = new Organisation();
    org.OrgId = authOrg.OrgId;
    org.OrgName = authOrg.OrgName;
    org.IsActive = authOrg.IsActive;
    org.OrgUnits = authOrg.OrgUnits?.map(unit => ({
      UnitId: unit.UnitId,
      UnitName: unit.UnitName,
      IsActive: unit.IsActive,
      IsSelected: unit.IsSelected || true
    })) || [];
    return org;
  }

  private redirectToLogin(): void {
    this.router.navigate(['/login']);
  }

  processAppConfig(): void {
    this.appctx.AppConfigurations = new ApplicationConfiguration();
    this.appctx.AppConfigurations.Settings = [];
    this.appctx.AppConfigurations.CustomSettings = {};
    this.appctx.AppConfigurations.CustomSettings.MergeFullName = true;
  }

  validateAccess(requestedUrl: string): void {
    let originalUrl = requestedUrl;
    let hasViewAccess = false;

    requestedUrl = decodeURIComponent(requestedUrl).replace(/^\//, '');
    requestedUrl = requestedUrl.split('?')[0].split('/edit')[0].split('/upgrade')[0];
    let urlPart = requestedUrl.split('/')[0];

    const publicRoutes = ['restrictedaccess', 'redirector', 'login', 'org-selector', 'org-input', 'core/userbirthdays', 'core/ptattendance'];
    if (publicRoutes.includes(urlPart)) {
      return;
    }

    if (this.navItems?.length > 0) {
      this.navItems.forEach(t => {
        if (t.RoutingUrl === requestedUrl || t.RoutingUrl === urlPart) {
          hasViewAccess = true;
          this.appctx.CurrentScreen = this.currentScreen = t;
        }

        if (t.children?.length > 0) {
          t.children.forEach(c => {
            if (c.RoutingUrl === requestedUrl || c.RoutingUrl === urlPart) {
              hasViewAccess = true;
              this.appctx.CurrentScreen = this.currentScreen = c;
            }
          });
        }
      });

      if (!hasViewAccess && !publicRoutes.includes(requestedUrl)) {
        this.router.navigate(['/restrictedaccess']);
      }
    }
  }

  public Load(): void {
    Global.Log('[AppComponent] Load called');

    if (!this.appctx.CurrentOrg?.OrgId || !this.appctx.CurrentUser?.ID) {
      Global.LogWarning('[AppComponent] Organization or User not initialized');
      return;
    }

    this.indLoading = true;
    this.loadingCounter = 2;
    this.cdr.markForCheck();

    this.loadNavigationMenu();
    this.loadApplicationConfig();
  }

  private loadNavigationMenu(): void {
    const url = Global.BASE_USER_ENDPOINT() + Global.ScreensController +
      "GetNavigationMenu/?userId=" + this.appctx.CurrentUser.ID + "&filter=";

    Global.Log('[AppComponent] Loading navigation menu');

    this._service
      .getData(url)
      .then((response: any) => {
        let menuItems: Screen[] = [];
        if (response?.ListItems) {
          menuItems = response.ListItems;
        } else if (Array.isArray(response)) {
          menuItems = response;
        }

        if (menuItems?.length > 0) {
          this.bindNavigation(menuItems);
        }

        this.decrementLoadingCounter();
      })
      .catch(error => {
        Global.LogError('[AppComponent] Error loading navigation:', error);
        this.decrementLoadingCounter();
      });
  }

  private loadApplicationConfig(): void {
    const url = Global.BASE_USER_ENDPOINT() + Global.ApplicationController;

    Global.Log('[AppApplication] Loading application config');

    this._service
      .getData(url)
      .then(() => {
        Global.Log('[AppComponent] Application config loaded');
        this.decrementLoadingCounter();
      })
      .catch(error => {
        Global.LogError('[AppComponent] Error loading application config:', error);
        this.decrementLoadingCounter();
      });
  }

  private decrementLoadingCounter(): void {
    this.loadingCounter--;

    if (this.loadingCounter <= 0) {
      this.stopLoading();
    }
  }

  private bindNavigation(menuItems: Screen[]): void {
    Global.Log('[AppComponent] Binding navigation items');
    this.navItems = [...menuItems];
    this.updateMenuSelection(this.router.url);
    this.cdr.markForCheck();
  }

  private stopLoading(): void {
    Global.Log('[AppComponent] Navigation menu loaded, hiding overlay');
    this.indLoading = false;
    this.cdr.markForCheck();
  }

  reset(): void {
    this.isUserIdle = false;
    this.idleUserService.reset();
  }

  onActivate($event: any): void { }
  onDeactivate($event: any): void { }
  onAttach($event: any): void { }
  onDetach($event: any): void { }
}

// PIPE for filter
import { Pipe, PipeTransform } from '@angular/core';
@Pipe({
  name: 'visibilityfilter',
  pure: true
})
export class VisibilityFilterPipe implements PipeTransform {
  transform(items: any[], filter: Object): any {
    if (!items || !filter) {
      return items;
    }
    return items.filter(item => item.IsVisible === true);
  }
}

